import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { Footer, Header, Page, PageContent } from "@/components/page.tsx";
import { formatCurrency } from "@/lib/utils.ts";
import { useShop } from "@/hooks/use-shop.ts";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/spinner";
import {
  useRecyclerFillStatus,
  useStopRecyclerFillSession,
} from "@/hooks/use-recycler-fill-status";

export const Route = createFileRoute("/recycler-refill")({
  component: RouteComponent,
});

function RouteComponent() {
  useTranslation();
  const { data: shop } = useShop();
  const navigate = useNavigate();

  const {
    data: status,
    isLoading,
    error: queryError,
    isError,
  } = useRecyclerFillStatus({ refetchInterval: 250 });

  const stopMutation = useStopRecyclerFillSession();

  const handleStop = async () => {
    try {
      await stopMutation.mutateAsync();
      navigate({ to: "/start-screen" });
    } catch (e) {
      console.error("Failed to stop session:", e);
    }
  };

  const error = status?.error ?? (isError ? queryError?.message : null);

  // The recycler holds 1000 HUF notes (or 10 EUR notes); the status is keyed by
  // that display denomination.
  const recyclerNoteValue = shop?.currencyCode === "HUF" ? 1000 : 10;
  const sessionInsertedCount =
    status?.sessionInserted?.[String(recyclerNoteValue)] ?? 0;
  const sessionInsertedAmount = sessionInsertedCount * recyclerNoteValue;
  const totalRecyclerCount =
    status?.totalRecycler?.[String(recyclerNoteValue)] ?? 0;
  const totalRecyclerAmount = totalRecyclerCount * recyclerNoteValue;

  return (
    <Page className={`bg-background`} skipInactvityModal>
      {stopMutation.isPending && <LoadingSpinner />}
      <Header backArrowHref={null}>
        <div className="text-center">
          <h1 className="text-white text-4xl font-bold">
            <Trans>Recycler feltöltés</Trans>
          </h1>
          {isLoading && (
            <p className="text-amber-300 text-sm mt-2">
              <Trans>Kapcsolódás...</Trans>
            </p>
          )}
        </div>
      </Header>
      <PageContent>
        <h2 className={`font-helvetica font-bold text-5xl text-center mb-8`}>
          <Trans>Kérjük helyezze be a 10 eurós bankjegyeket!</Trans>
        </h2>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-8 text-center">
            <p className="font-bold">
              <Trans>Hiba</Trans>
            </p>
            <p>{error}</p>
          </div>
        )}

        <div className={`grid grid-cols-2 justify-center gap-x-8 mb-12`}>
          <div
            className={`text-center text-4xl bg-white rounded-2xl shadow-lg p-8`}
          >
            <p className={`mb-4 text-gray-600`}>
              <Trans>Ebben a munkamenetben behelyezve</Trans>
            </p>
            <p className={`font-helvetica font-bold text-5xl text-green-600`}>
              {formatCurrency(
                sessionInsertedAmount,
                shop?.currencyCode ?? "EUR",
              )}
            </p>
            <p className={`text-2xl text-gray-500 mt-2`}>
              {sessionInsertedCount} <Trans>db bankjegy</Trans>
            </p>
          </div>
          <div
            className={`text-center text-4xl bg-white rounded-2xl shadow-lg p-8`}
          >
            <p className={`mb-4 text-gray-600`}>
              <Trans>Recycler összesen</Trans>
            </p>
            <p className={`font-helvetica font-bold text-5xl text-blue-600`}>
              {formatCurrency(totalRecyclerAmount, shop?.currencyCode ?? "EUR")}
            </p>
            <p className={`text-2xl text-gray-500 mt-2`}>
              {totalRecyclerCount} <Trans>db bankjegy</Trans>
            </p>
          </div>
        </div>

        <div className={`flex justify-center mb-16`}>
          <Button
            className={`mx-auto text-2xl px-16 py-8`}
            onClick={handleStop}
            disabled={stopMutation.isPending}
            variant="orange"
          >
            <Trans>Feltöltés befejezése</Trans>
          </Button>
        </div>

        <div className={`bg-blue-50 rounded-xl p-6 text-center`}>
          <p className={`text-xl text-blue-800`}>
            <Trans>
              A recycler csak 10 eurós bankjegyeket fogad el. A behelyezett
              bankjegyek automatikusan a recyclerbe kerülnek.
            </Trans>
          </p>
        </div>
      </PageContent>

      <div className={`grid grid-cols-1 gap-x-8 bg-[#33c0cd] p-10`}>
        <div className={`flex justify-center items-center gap-x-10`}>
          <img src={`/paper-cash.svg`} className={`w-[120px]`} />
          <div>
            <p className={`text-white text-2xl leading-10`}>
              <Trans>
                Helyezze be a 10 eurós bankjegyeket a bankjegy elfogadóba.
              </Trans>
            </p>
          </div>
        </div>
      </div>

      <Footer
        helpText={
          <Trans>
            Ez a képernyő a recycler feltöltésére szolgál. A behelyezett
            bankjegyek automatikusan a recyclerbe kerülnek.
          </Trans>
        }
      />
    </Page>
  );
}
